import { ConfirmDialog } from "@/components/common/ConfirmDialog/ConfirmDialog"
import { placeholderLines } from "@/features/settings/setup/placeholders"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconAlertTriangle } from "@tabler/icons-react"
import type { InquiryTemplateSettings } from "../settings/templateSettingsMock"
import type { DraftSettingsTarget } from "./DraftSettingsSheet"
import type { AttachmentFormat, Lang } from "./inquiryDraftMock"

/** The sign-off lines still holding placeholder text, by the setting that holds them. */
export interface SignoffLines {
  signature: string[]
  footer: string[]
}

/**
 * The sign-off a send in `language` and `format` delivers, as the backend picks it
 * (`_signature`, `_require_written_signoff`): the signature, falling back to EN, and
 * the footer only when the PDF-only questionnaire prints it.
 */
export function sentSignoff(
  settings: InquiryTemplateSettings,
  language: Lang,
  format: AttachmentFormat,
): { signature: string; footer: string | null } {
  return {
    signature:
      settings.emailSignatures[language] || settings.emailSignatures.en || "",
    footer: format === "pdf" ? settings.questionnaireFooter : null,
  }
}

/** What the send would refuse as SIGNATURE_PLACEHOLDER, or null when nothing. */
export function signoffLinesToConfirm(
  settings: InquiryTemplateSettings,
  language: Lang,
  format: AttachmentFormat,
): SignoffLines | null {
  const { signature, footer } = sentSignoff(settings, language, format)
  const lines = {
    signature: [...new Set(placeholderLines(signature))],
    footer: [...new Set(placeholderLines(footer ?? ""))],
  }
  return lines.signature.length + lines.footer.length > 0 ? lines : null
}

/** Asks before a send whose signature or footer still holds placeholder text. */
export function SignoffPlaceholderDialog({
  lines,
  onSendAnyway,
  onEdit,
  onCancel,
  isLoading,
}: {
  lines: SignoffLines
  onSendAnyway: () => void
  onEdit: (target: DraftSettingsTarget) => void
  onCancel: () => void
  isLoading?: boolean
}) {
  const { translate } = useTranslation()
  const groups = [
    {
      target: "email.signature" as const,
      label: translate("MC_INQ_SIGNOFF_SIGNATURE"),
      edit: translate("MC_INQ_DRAFT_EDIT_SIGNATURE"),
      lines: lines.signature,
    },
    {
      target: "inquiry.footer" as const,
      label: translate("MC_INQ_SIGNOFF_FOOTER"),
      edit: translate("MC_INQ_SIGNOFF_EDIT_FOOTER"),
      lines: lines.footer,
    },
  ].filter((group) => group.lines.length > 0)
  return (
    <ConfirmDialog
      variant="warning"
      icon={IconAlertTriangle}
      title={translate("MC_INQ_DRAFT_PLACEHOLDERS_TITLE")}
      message={translate("MC_INQ_SIGNOFF_DESC")}
      details={
        <div className="flex flex-col gap-3">
          {groups.map((group) => (
            <div key={group.target} className="flex flex-col gap-1.5">
              <span className="text-[12px] font-medium text-grey5">
                {group.label}
              </span>
              <ul className="font-mono rounded border border-grey4 bg-grey2 px-3 py-2 text-[12px] text-grey5">
                {group.lines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              <button
                type="button"
                disabled={isLoading}
                onClick={() => onEdit(group.target)}
                className="self-start text-[12.5px] font-medium text-yellow1 hover:underline disabled:no-underline disabled:opacity-50"
              >
                {group.edit}
              </button>
            </div>
          ))}
        </div>
      }
      confirmLabel={translate("MC_INQ_SIGNOFF_SEND_ANYWAY")}
      cancelLabel={translate("MC_INQ_DRAFT_BTN_CANCEL")}
      onConfirm={onSendAnyway}
      onCancel={onCancel}
      isLoading={isLoading}
    />
  )
}
